import {
    listEmpleados as repoListEmpleados,
    createEmpleado as repoCreateEmpleado,
    updateEmpleado as repoUpdateEmpleado,
    setEmpleadoPin as repoSetEmpleadoPin,
} from '../infrastructure/empleados.repository.js';
import {
    getFudoCustomers,
    getFudoProviders,
    invalidateFudoDirectoryCache as repoInvalidateFudoDirectoryCache,
} from '../infrastructure/fudo-directory.repository.js';

/**
 * Servicio de empleados para el panel de staff (Control de Horarios y
 * Liquidación de Sueldos, slice 1). Orquesta el repositorio (I/O); no
 * contiene lógica de Supabase.
 *
 * `valor` y `pin` son campos sensibles (Req: Sensitive Field Isolation). Este
 * módulo nunca reintroduce un PIN en texto plano en ningún objeto devuelto:
 * `createEmpleado`/`updateEmpleado` jamás reciben ni pasan el PIN (viaja
 * únicamente por `setEmpleadoPin`, que llama a la RPC `set_empleado_pin` —
 * el hash nunca sale de Postgres), y ni siquiera ese camino devuelve el
 * valor que se mandó.
 */

const TIPOS_PAGO_VALIDOS = ['hora', 'dia', 'fijo'];
/**
 * Owner decision 2026-09-02: el PIN es de EXACTAMENTE 4 dígitos (antes
 * 4-8). Solo cambia esta validación de capa de aplicación — las RPCs
 * `set_empleado_pin`/`kiosk_fichar` (migraciones 17/18) siguen aceptando
 * 4-8 en SQL sin migración nueva, porque hoy no existe ningún empleado con
 * un PIN de más de 4 dígitos (verificado antes de este cambio); un PIN
 * viejo de 5-8 dígitos seguiría validando contra el hash existente si
 * alguna vez existiera uno, pero ya no se puede CREAR uno nuevo por acá ni
 * por `PinPad.js`/`MAX_PIN_LEN` (4).
 */
const PIN_FORMAT = /^\d{4}$/;
const UNIQUE_VIOLATION = '23505';

/**
 * Migration 21's partial unique indexes (AD13), keyed by INDEX NAME rather
 * than by which field the caller sent: the index covers active AND inactive
 * employees, so there is nothing else in a raw 23505 to distinguish "this
 * employee's Fudo id" from "some other employee's Fudo id" — matching on the
 * index name is also what keeps this mapping from ever firing on an
 * unrelated unique_violation.
 */
const FUDO_UNIQUE_INDEX_MESSAGES = {
    empleados_fudo_cliente_unico: 'Ese cliente Fudo ya está asignado a otro empleado.',
    empleados_fudo_proveedor_unico: 'Ese proveedor Fudo ya está asignado a otro empleado.',
};

/**
 * Maps a 23505 raised by one of migration 21's Fudo identity indexes to a
 * readable message (Req: "Fudo Identifier Assignment Is Validated and
 * Exclusive"). Returns `null` for any other error, including an unrelated
 * unique_violation — callers must rethrow in that case, never swallow it.
 * @param {{ code?: string, message?: string }} error
 * @returns {string|null}
 */
function mapFudoIdentityViolation(error) {
    if (error?.code !== UNIQUE_VIOLATION) return null;
    for (const [indexName, message] of Object.entries(FUDO_UNIQUE_INDEX_MESSAGES)) {
        if (error.message?.includes(indexName)) return message;
    }
    return null;
}

const PIN_ERROR_MESSAGES = {
    pin_formato_invalido: 'El PIN debe tener 4 dígitos.',
    // El PIN de un empleado dado de baja queda reservado (ver migración 17): el
    // conflicto puede ser con alguien inactivo, así que el mensaje no afirma
    // que el otro empleado esté activo.
    pin_duplicado: 'Ese PIN ya está en uso por otro empleado.',
    empleado_inexistente: 'El empleado no existe.',
};

/**
 * @typedef {{ ok: boolean, error?: string, empleado?: object }} EmpleadoResult
 */

/**
 * Valida los campos de empleado sujetos a `Requirement: Employee Record
 * Fields` / `Required fields enforced`. En modo `partial` (edición), un
 * campo ausente se considera "sin cambios" y no se valida; si está
 * presente, sí se valida igual que en creación.
 * @param {object} fields
 * @param {{ partial?: boolean }} [opts]
 * @returns {string|null} mensaje de error, o `null` si es válido
 */
function validateEmpleadoFields(fields, { partial = false } = {}) {
    const hasTipoPago = Object.hasOwn(fields, 'tipo_pago');
    if (!partial && !hasTipoPago) {
        return 'El tipo_pago es obligatorio (hora, dia o fijo).';
    }
    if (hasTipoPago && !TIPOS_PAGO_VALIDOS.includes(fields.tipo_pago)) {
        return 'tipo_pago inválido: debe ser "hora", "dia" o "fijo".';
    }

    const hasValor = Object.hasOwn(fields, 'valor');
    if (!partial && !hasValor) {
        return 'El valor es obligatorio.';
    }
    if (hasValor && (fields.valor === null || fields.valor === undefined || Number(fields.valor) < 0)) {
        return 'El valor no puede ser negativo.';
    }

    return null;
}

/**
 * Trae todos los empleados (activos e inactivos).
 * @returns {Promise<Array<object>>}
 * @throws {Error} si el repositorio falla
 */
export async function loadEmpleados() {
    return repoListEmpleados();
}

/**
 * Trae los clientes/proveedores Fudo para los pickers de A2b (design.md
 * "Pickers (slice A2)"). El `accessToken` es el de la sesión de staff
 * activa: `/customers`/`/providers` quedan gateados por `requireAdmin`
 * desde A2a (AD14).
 * @param {{ accessToken: string, forceRefresh?: boolean }} params
 * @returns {Promise<{ customers: Array<object>, providers: Array<object> }>}
 * @throws {Error} si `accessToken` falta o la lectura falla
 */
export async function loadFudoDirectory({ accessToken, forceRefresh = false } = {}) {
    const [customers, providers] = await Promise.all([
        getFudoCustomers({ accessToken, forceRefresh }),
        getFudoProviders({ accessToken, forceRefresh }),
    ]);
    return { customers, providers };
}

/**
 * Review follow-up P4 (2026-08-08): re-exported so `js/admin.js`'s logout
 * path can drop the module-level Fudo directory cache without importing the
 * infrastructure layer directly.
 */
export function invalidateFudoDirectoryCache() {
    repoInvalidateFudoDirectoryCache();
}

/**
 * Crea un empleado nuevo, validando `tipo_pago` y `valor` antes de tocar el
 * repositorio (Req: Required fields enforced — "no partial record is
 * created").
 * @param {object} input
 * @returns {Promise<EmpleadoResult>}
 */
export async function createEmpleado(input) {
    const error = validateEmpleadoFields(input, { partial: false });
    if (error) return { ok: false, error };

    try {
        const empleado = await repoCreateEmpleado(input);
        return { ok: true, empleado };
    } catch (repoError) {
        const fudoMessage = mapFudoIdentityViolation(repoError);
        if (fudoMessage) return { ok: false, error: fudoMessage };
        throw repoError;
    }
}

/**
 * Edita un empleado existente (campos parciales). Solo valida los campos
 * que vienen presentes en `fields`.
 * @param {string} id
 * @param {object} fields
 * @returns {Promise<EmpleadoResult>}
 */
export async function updateEmpleado(id, fields) {
    const error = validateEmpleadoFields(fields, { partial: true });
    if (error) return { ok: false, error };

    try {
        const empleado = await repoUpdateEmpleado(id, fields);
        return { ok: true, empleado };
    } catch (repoError) {
        const fudoMessage = mapFudoIdentityViolation(repoError);
        if (fudoMessage) return { ok: false, error: fudoMessage };
        throw repoError;
    }
}

/**
 * @typedef {{ ok: boolean, error?: string }} SetPinResult
 */

/**
 * Setea el PIN de un empleado. Valida formato ANTES de llamar a la RPC (el
 * formato también se valida server-side, esto es solo para no gastar el
 * round-trip); el rechazo por duplicado (Req: Duplicate PIN rejected) llega
 * como el `error` inline de la RPC y se traduce a un mensaje legible.
 * @param {{ empleadoId?: string, pin?: string }} params
 * @returns {Promise<SetPinResult>}
 */
export async function setEmpleadoPin({ empleadoId, pin } = {}) {
    if (!empleadoId) {
        return { ok: false, error: 'Empleado inválido.' };
    }
    if (!pin || !PIN_FORMAT.test(pin)) {
        return { ok: false, error: PIN_ERROR_MESSAGES.pin_formato_invalido };
    }

    const result = await repoSetEmpleadoPin({ empleadoId, pin });
    if (!result?.ok) {
        return { ok: false, error: PIN_ERROR_MESSAGES[result?.error] ?? 'No pudimos guardar el PIN.' };
    }
    return { ok: true };
}
